import {
  BorrowerOnboardingDetails,
  Loan,
  OnboardingDetailsRole,
  patchUpdateBorrowerDetails,
} from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { useCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useBorrowerOnboardingDetails';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { patchNieDataIfRequired } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/utils/patchNieDataIfRequired';
import { MerchantPos } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPos';
import { useRequest } from '@/hooks/requests/useRequest';

interface EditOnBoardingDetailsDialogProps {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  role: OnboardingDetailsRole.BORROWER | OnboardingDetailsRole.CO_BORROWER;
  opened: boolean;
  setOpened: (opened: boolean) => void;
}

type EditOnBoardingDetailsDto = Parameters<typeof patchUpdateBorrowerDetails>[0];

export const EditOnBoardingDetailsDialog = ({
  paymentMethodDetailEntity,
  role,
  opened,
  setOpened,
}: EditOnBoardingDetailsDialogProps) => {
  const loan = paymentMethodDetailEntity.loan;

  if (!loan) {
    return null;
  }

  const onboardingDetails = loan.onboardingDetails.find((participant) => participant.role === role);

  if (!onboardingDetails) {
    return null;
  }

  return (
    <EditOnboardingDetailsDialogContent
      loan={loan}
      borrowerOnboardingDetails={onboardingDetails as BorrowerOnboardingDetails}
      paymentMethodDetailEntity={paymentMethodDetailEntity}
      role={role}
      opened={opened}
      setOpened={setOpened}
    />
  );
};

interface EditOnboardingDetailsDialogContentProps {
  loan: Loan;
  borrowerOnboardingDetails: BorrowerOnboardingDetails;
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  role: OnboardingDetailsRole.BORROWER | OnboardingDetailsRole.CO_BORROWER;
  opened: boolean;
  setOpened: (opened: boolean) => void;
}

const EditOnboardingDetailsDialogContent = ({
  loan,
  borrowerOnboardingDetails,
  paymentMethodDetailEntity,
  role,
  opened,
  setOpened,
}: EditOnboardingDetailsDialogContentProps) => {
  const { checkoutSessionEntity, refreshCheckoutSessionAndPaymentMethodEntities } = useCheckoutSession();
  const { request } = useRequest();
  const { borrowerOnboardingDetailsWithIncome, revalidateIncome } = useBorrowerOnboardingDetails({
    loan,
    borrowerOnboardingDetails,
    role,
  });

  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      setError(null);
      throw error;
    }
  }, [error]);

  const paymentIntentId = paymentMethodDetailEntity.paymentIntentId;

  if (!checkoutSessionEntity || !paymentIntentId) {
    return null;
  }

  const onSaveEditOnboardingDetails = async (onboardingDetails: StoredBorrowerOnboardingDetails) => {
    if (!onboardingDetails) {
      throw new Error(`${role} onboarding details are required`);
    }

    try {
      const editBorrowerDto: EditOnBoardingDetailsDto = {
        ...onboardingDetails,
        id: checkoutSessionEntity.id,
        paymentIntentId: paymentIntentId,
        onboardingDetailsId: borrowerOnboardingDetails.id,
        role: role,
      };

      await request(patchUpdateBorrowerDetails, editBorrowerDto);
      await patchNieDataIfRequired({
        onboardingDetails,
        onboardingDetailsId: borrowerOnboardingDetails.id,
        paymentMethodDetailEntity,
      });
      await refreshCheckoutSessionAndPaymentMethodEntities();
      revalidateIncome();
      setOpened(false);
    } catch (caughtError) {
      setOpened(false);
      setError(caughtError as Error);
    }
  };

  if (!borrowerOnboardingDetailsWithIncome) {
    return null;
  }

  return (
    <MerchantPos
      opened={opened}
      setOpened={setOpened}
      paymentMethodDetailEntity={paymentMethodDetailEntity}
      checkoutSessionDetailEntity={checkoutSessionEntity}
      selectedRole={role}
      borrowerOnboardingDetails={[borrowerOnboardingDetailsWithIncome]}
      onSaveDetails={onSaveEditOnboardingDetails}
    />
  );
};
